import React, { useState, useEffect } from 'react';
import { customersService } from '../services';
import DashboardLayout from '../components/DashboardLayout';
import LoadingSpinner from '../components/LoadingSpinner';
import CustomerDetail from '../components/CustomerDetail';
import Modal from '../components/Modal';
import { formatCurrency, formatDate } from '../utils/formatters';
import PageHeader from '../components/ui/PageHeader';
import StatGrid from '../components/ui/StatGrid';
import FilterPanel from '../components/ui/FilterPanel';
import ListCard, { ListCards, CardAction } from '../components/ui/ListCard';

const Customers = () => {
  const [customers, setCustomers] = useState([]);
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);

  // Filters and search
  const [filters, setFilters] = useState({
    location: '',
    startDate: '',
    endDate: '',
    sortBy: 'totalSpent',
    sortOrder: 'desc'
  });
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchCustomers();
    fetchOverview();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  const fetchCustomers = async () => {
    try {
      setLoading(true);
      const filterParams = { ...filters };

      const result = await customersService.getAll(filterParams);
      setCustomers(result.customers || []);
    } catch (err) {
      console.error('Error fetching customers:', err);
      setCustomers([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchOverview = async () => {
    try {
      const filterParams = {};
      if (filters.startDate) filterParams.startDate = filters.startDate;
      if (filters.endDate) filterParams.endDate = filters.endDate;
      if (filters.location) filterParams.location = filters.location;

      const data = await customersService.getOverview(filterParams);
      setOverview(data);
    } catch (err) {
      console.error('Error fetching overview:', err);
    }
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const clearFilters = () => {
    setFilters({
      location: '',
      startDate: '',
      endDate: '',
      sortBy: 'totalSpent',
      sortOrder: 'desc'
    });
    setSearchTerm('');
  };

  const handleCustomerClick = async (customer) => {
    try {
      setSelectedCustomer(customer);
      setShowDetailModal(true);
    } catch (err) {
      console.error('Error loading customer details:', err);
    }
  };

  // Filter customers by search term
  const filteredCustomers = customers.filter(customer => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      customer.name?.toLowerCase().includes(search) ||
      customer.phone?.includes(search) ||
      customer.location?.toLowerCase().includes(search)
    );
  });

  // Get unique locations from customers
  const locations = [...new Set(customers.map(c => c.location).filter(Boolean))];

  const getFrequencyBadgeColor = (label) => {
    switch (label) {
      case 'Frequent':
        return 'bg-green-100 text-green-800';
      case 'Regular':
        return 'bg-blue-100 text-blue-800';
      case 'Occasional':
        return 'bg-yellow-100 text-yellow-800';
      case 'Rare':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-purple-100 text-purple-800';
    }
  };

  const activeFilters = ['location', 'startDate', 'endDate'].filter((k) => filters[k]).length;

  return (
    <DashboardLayout>
      <div className="space-y-4 md:space-y-6">
        <PageHeader title="Customers" subtitle="Customer list and what each one buys" />

        {overview && (
          <StatGrid
            items={[
              { label: 'Customers', value: overview.totalCustomers },
              { label: 'Revenue', value: formatCurrency(overview.totalRevenue), tone: 'good' },
              { label: 'Avg per customer', value: formatCurrency(overview.averageCustomerValue) },
              { label: 'New this month', value: overview.newCustomersThisMonth },
            ]}
          />
        )}

        <FilterPanel
          activeCount={activeFilters}
          columns="lg:grid-cols-6"
          search={
            <input
              type="search"
              aria-label="Search customers"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search customers"
              className="w-full h-11 border-gray-300 rounded-lg shadow-sm focus:ring-blue-500 focus:border-blue-500"
            />
          }
        >
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Location</label>
            <select name="location" value={filters.location} onChange={handleFilterChange} className="w-full h-11 border-gray-300 rounded-lg shadow-sm focus:ring-blue-500 focus:border-blue-500">
              <option value="">All locations</option>
              {locations.map((loc) => <option key={loc} value={loc}>{loc}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">From</label>
            <input type="date" name="startDate" value={filters.startDate} onChange={handleFilterChange} className="w-full h-11 border-gray-300 rounded-lg shadow-sm focus:ring-blue-500 focus:border-blue-500" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">To</label>
            <input type="date" name="endDate" value={filters.endDate} onChange={handleFilterChange} className="w-full h-11 border-gray-300 rounded-lg shadow-sm focus:ring-blue-500 focus:border-blue-500" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Sort by</label>
            <select name="sortBy" value={filters.sortBy} onChange={handleFilterChange} className="w-full h-11 border-gray-300 rounded-lg shadow-sm focus:ring-blue-500 focus:border-blue-500">
              <option value="totalSpent">Total spent</option>
              <option value="totalPurchases">Purchase count</option>
              <option value="lastPurchaseDate">Last purchase</option>
              <option value="firstPurchaseDate">First purchase</option>
              <option value="name">Name</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Order</label>
            <select name="sortOrder" value={filters.sortOrder} onChange={handleFilterChange} className="w-full h-11 border-gray-300 rounded-lg shadow-sm focus:ring-blue-500 focus:border-blue-500">
              <option value="desc">High to low</option>
              <option value="asc">Low to high</option>
            </select>
          </div>
          <div className="flex items-end">
            <button type="button" onClick={clearFilters} className="w-full h-11 px-4 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 text-sm font-medium">
              Clear
            </button>
          </div>
        </FilterPanel>

        {/* Customers Table */}
        <div className={!loading && filteredCustomers.length > 0
          ? 'md:bg-white md:rounded-lg md:shadow md:overflow-hidden'
          : 'bg-white rounded-2xl border border-slate-200 md:border-0 md:rounded-lg md:shadow overflow-hidden'}>
          {loading ? (
            <div className="flex justify-center items-center py-12">
              <LoadingSpinner />
            </div>
          ) : filteredCustomers.length === 0 ? (
            <div className="text-center py-12">
              <h3 className="text-lg font-medium text-gray-900 mb-2">No customers found</h3>
              <p className="text-gray-500">Customers will appear here once sales are recorded.</p>
            </div>
          ) : (
            <>
            <ListCards>
              {filteredCustomers.map((customer) => (
                <ListCard
                  key={customer.phone}
                  title={customer.name}
                  subtitle={[customer.phone, customer.location].filter(Boolean).join(' · ')}
                  amount={formatCurrency(customer.totalSpent)}
                  amountTone="text-emerald-600"
                  meta={`${customer.totalPurchases} purchases · last ${customer.lastPurchaseDate ? formatDate(customer.lastPurchaseDate) : '-'}`}
                  badge={customer.purchaseFrequencyLabel && (
                    <span className={`inline-flex px-2 py-0.5 text-xs font-semibold rounded-full ${getFrequencyBadgeColor(customer.purchaseFrequencyLabel)}`}>
                      {customer.purchaseFrequencyLabel}
                    </span>
                  )}
                  actions={
                    <>
                      {customer.phone && <CardAction href={`tel:${customer.phone}`}>Call</CardAction>}
                      <CardAction tone="primary" onClick={() => handleCustomerClick(customer)}>Details</CardAction>
                    </>
                  }
                />
              ))}
            </ListCards>
            <div className="hidden md:block overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Phone</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Location</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Total Spent</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Purchases</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Last Purchase</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Frequency</th>
                    <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredCustomers.map((customer) => (
                    <tr
                      key={customer.phone}
                      className="hover:bg-gray-50 cursor-pointer"
                      onClick={() => handleCustomerClick(customer)}
                    >
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">{customer.name}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{customer.phone}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-500">{customer.location || '-'}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right">
                        <div className="text-sm font-medium text-green-600">
                          {formatCurrency(customer.totalSpent)}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right">
                        <div className="text-sm text-gray-900">{customer.totalPurchases}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-500">
                          {customer.lastPurchaseDate ? formatDate(customer.lastPurchaseDate) : '-'}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getFrequencyBadgeColor(customer.purchaseFrequencyLabel)}`}>
                          {customer.purchaseFrequencyLabel}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleCustomerClick(customer);
                          }}
                          className="text-blue-600 hover:text-blue-900 text-sm font-medium"
                        >
                          View Details
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            </>
          )}
        </div>

        {/* Customer Detail Modal */}
        {selectedCustomer && (
          <Modal
            isOpen={showDetailModal}
            onClose={() => {
              setShowDetailModal(false);
              setSelectedCustomer(null);
            }}
            title={selectedCustomer.name}
            size="xl"
          >
            <CustomerDetail customer={selectedCustomer} />
          </Modal>
        )}
      </div>
    </DashboardLayout>
  );
};

export default Customers;

